/**
 * SSE 구독 헬퍼 — `/events/{topic}` 에 붙는다.
 *
 * ★ EventSource 는 **상태 코드를 읽을 수 없다.** 401 이든 503 이든 `error`
 *   이벤트 하나로 온다. 그래서 "다시 붙어도 소용없는 실패" 와 "잠깐 끊긴 것" 을
 *   구분할 방법이 브라우저에는 없다.
 *
 *   예전에는 그 둘을 안 가리고 **영원히** 다시 붙었다(백오프가 30초에서 멈춘다).
 *   로그아웃 상태로 화면을 열어 두면 콘솔에 `GET /events/demo 401` 이 30초마다
 *   끝없이 쌓였다. 서버 로그도 같이 쌓인다.
 *
 *   판단 기준을 하나 둔다 — **한 번이라도 열렸는가.**
 *     · 열린 적이 있다 → 진짜 끊김이다. 계속 다시 붙는다(재연결이 목적이다)
 *     · 한 번도 못 열었다 → 권한·경로 문제다. 몇 번 해 보고 **포기한다**
 */
type Handler = (payload: unknown) => void;

/** 한 번도 못 열었을 때 몇 번까지 해 볼지. 이 뒤로는 포기하고 조용해진다. */
const MAX_COLD_TRIES = 3;

export class NxEventStream {
  private source: EventSource | null = null;
  private handlers = new Set<Handler>();
  private retryMs = 1000;
  private everOpened = false;
  private coldTries = 0;
  private givenUp = false;
  private timer = 0;

  constructor(private readonly topic: string) {}

  on(handler: Handler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  /** 붙어 본 적은 있는데 끝내 못 열고 포기했는가. 화면이 상태를 표시할 때 쓴다. */
  get failed(): boolean {
    return this.givenUp;
  }

  start(): void {
    if (this.source || this.givenUp) return;
    const src = new EventSource(`/events/${encodeURIComponent(this.topic)}`);
    this.source = src;

    src.addEventListener('message', (ev) => {
      let payload: unknown = (ev as MessageEvent).data;
      try {
        payload = JSON.parse((ev as MessageEvent).data as string);
      } catch {
        /* 문자열 그대로 전달 */
      }
      this.handlers.forEach((h) => h(payload));
    });

    src.addEventListener('open', () => {
      this.everOpened = true;
      this.coldTries = 0;
      this.retryMs = 1000;
    });

    src.addEventListener('error', () => {
      src.close();
      this.source = null;

      if (!this.everOpened) {
        this.coldTries += 1;
        if (this.coldTries >= MAX_COLD_TRIES) {
          this.givenUp = true;
          // 한 번만 남긴다 — 30초마다 콘솔을 채우던 것이 이 자리였다.
          console.warn(
            `[nxEvents] '${this.topic}' 에 붙지 못했습니다(${this.coldTries}회). ` +
              `로그인이 필요한 토픽일 수 있습니다 — 더 시도하지 않습니다.`,
          );
          this.handlers.forEach((h) => h({ nxError: 'unavailable', topic: this.topic }));
          return;
        }
      }

      const delay = this.retryMs;
      this.retryMs = Math.min(this.retryMs * 2, 30000);
      this.timer = window.setTimeout(() => this.start(), delay);
    });
  }

  stop(): void {
    window.clearTimeout(this.timer);
    this.source?.close();
    this.source = null;
  }
}

const streams = new Map<string, NxEventStream>();

export const NxEvents = {
  /**
   * 토픽을 구독한다. 구독을 끊는 함수를 돌려준다.
   *
   * ★ 로그인하지 않았으면 **붙지 않는다.** `/events/{topic}` 은 로그인 전용이라
   *   (`events.py`) 붙어 봐야 401 이고, EventSource 는 그걸 알 방법이 없어
   *   재시도만 쌓는다. 서버가 `<body data-user>` 로 내려준 값으로 미리 가른다.
   *   공개 토픽이 생기면 그때 `{ public: true }` 를 받도록 넓힌다.
   */
  subscribe(topic: string, handler: Handler): () => void {
    if (!document.body.dataset.user) {
      console.info(`[nxEvents] 로그인하지 않아 '${topic}' 을 구독하지 않습니다.`);
      handler({ nxError: 'login_required', topic });
      return () => {};
    }
    let stream = streams.get(topic);
    if (!stream) {
      stream = new NxEventStream(topic);
      streams.set(topic, stream);
    }
    const off = stream.on(handler);
    stream.start();
    return off;
  },
};
